import { trackDesignSystemAction, trackSceneAction } from 'analytics';
import CollapsibleSection from 'components/Widget/CollapsibleSection';
import Icon, { IconType } from 'components/Icon';
import ProfileIcon from 'components/Panel/RightPanel/ProfileIcon';
import ProjectListItem from 'components/Sidebar/ListItem/ProjectListItem';
import { Loader } from 'components/Widget/Loader';
import Logo from 'components/Widget/Logo';
import { DesignSystem, Scene } from 'dragonmouse-shared/interfaces';
import { useSidebarWidth } from 'hooks/useSidebarWidth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
	selectDesignSystems,
	selectGuestDesignSystems,
	selectGuestScenes,
	selectIsGuest,
	selectOrganizationDesignSystemIds,
	selectOrganizations,
	selectOrganizationSceneIds,
	selectScenes,
	selectUser,
	selectUserDesignSystemIds,
	selectUserSceneIds,
} from 'redux/db/reducers';
import {
	setCursorOverSidebar,
	setForceTransientSidebarClosed,
	setForceTransientSidebarReleasePending,
	setSidebarDocked,
	setSidebarHovered,
	setSidebarVisible,
	toggleSidebarCollapsedSection,
} from 'redux/mode/actions';
import { selectIsMobile, selectSettings } from 'redux/mode/reducers';
import { RootReducer, selectIsLoadingAnything } from 'redux/reducers';
import * as routes from 'routes';
import { openInternalTab } from 'utils';
import { useTheme } from 'styled-components';
import AccountButton from './AccountButton';
import DesignSystemListItem from './ListItem/DesignSystemListItem';
import NewButton from './NewButton';
import * as S from './styles';

const SECTION_LIMIT = 5;
const SHOW_MORE_CHUNK = 20;

const Sidebar = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const navigate = useNavigate();
	const sidebarWidth = useSidebarWidth();

	const {
		sidebarVisible: sidebarVisibleFromState,
		sidebarDocked,
		sidebarHovered,
		forceTransientSidebarClosed,
		forceTransientSidebarReleasePending,
		cursorOverSidebar,
		contextMenu,
		scenes,
		guestScenes,
		designSystems,
		guestDesignSystems,
		user,
		isMobile,
		activeSceneId,
		activeDesignSystemId,
		loading,
		isGuest,
		organizations,
		organizationSceneIds,
		organizationDesignSystemIds,
		userSceneIds,
		userDesignSystemIds,
		collapsedSections,
	} = useSelector((r: RootReducer) => {
		return {
			sidebarVisible: r.mode.sidebarVisible,
			sidebarDocked: r.mode.sidebarDocked,
			sidebarHovered: r.mode.sidebarHovered,
			forceTransientSidebarClosed: r.mode.forceTransientSidebarClosed,
			forceTransientSidebarReleasePending: r.mode.forceTransientSidebarReleasePending,
			cursorOverSidebar: r.mode.cursorOverSidebar,
			collapsedSections: r.mode.sidebarCollapsedSections,
			contextMenu: r.mode.contextMenu,
			scenes: selectScenes(r.db),
			guestScenes: selectGuestScenes(r.db),
			designSystems: selectDesignSystems(r.db),
			guestDesignSystems: selectGuestDesignSystems(r.db),
			user: selectUser(r.db),
			isMobile: selectIsMobile(r.mode),
			activeSceneId: r.mode.activeSceneId,
			activeDesignSystemId: r.mode.activeDesignSystemId,
			loading: selectIsLoadingAnything(r),
			createOrganizationEnabled: selectSettings(r.mode).createOrganizationEnabled,
			isGuest: selectIsGuest(r.db),
			organizations: selectOrganizations(r.db),
			organizationSceneIds: selectOrganizationSceneIds(r.db),
			organizationDesignSystemIds: selectOrganizationDesignSystemIds(r.db),
			userSceneIds: selectUserSceneIds(r.db),
			userDesignSystemIds: selectUserDesignSystemIds(r.db),
		};
	}, shallowEqual);

	// Compute sidebar visibility: Mobile uses sidebarVisible directly. Desktop uses docked
	// or hovered — except when `forceTransientSidebarClosed` is set, which kills the transient
	// (hover-driven) component of visibility. Docked sidebars are unaffected.
	const transientHover = sidebarHovered && !forceTransientSidebarClosed;
	const sidebarVisible = isMobile ? sidebarVisibleFromState : sidebarDocked || transientHover;

	//----------------------------------------------------------------------------------------------
	// STATE MANAGEMENT
	//----------------------------------------------------------------------------------------------
	const [sectionLimits, setSectionLimits] = useState<Record<string, number>>({});
	const [filterText, setFilterText] = useState('');

	// Reset expanded limits when user selects an item
	useEffect(() => {
		setSectionLimits({});
	}, [activeSceneId, activeDesignSystemId]);

	// If a force-close release is pending and the cursor isn't over the sidebar (e.g. user
	// already moved away during the modal's lifetime), drop the flag immediately. Otherwise
	// the mouseLeave handler is responsible.
	useEffect(() => {
		if (forceTransientSidebarReleasePending && !cursorOverSidebar) {
			dispatch(setForceTransientSidebarClosed(false));
		}
	}, [forceTransientSidebarReleasePending, cursorOverSidebar, dispatch]);

	const onToggleCollapse = useCallback(
		(sectionKey: string) => {
			dispatch(toggleSidebarCollapsedSection(sectionKey));
		},
		[dispatch]
	);

	//----------------------------------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------------------------------

	const onLoadScene = (scene: Scene, newTab?: boolean) => {
		if (newTab) {
			trackSceneAction('open_in_new_tab');
			openInternalTab(routes.scene(scene.id, scene.title));
		} else {
			trackSceneAction('open');
			navigate(routes.scene(scene.id, scene.title));
			if (isMobile) {
				dispatch(setSidebarVisible(false));
			}
		}
	};

	const onLoadDesignSystem = (designSystem: DesignSystem, newTab?: boolean) => {
		if (newTab) {
			trackDesignSystemAction('open_in_new_tab');
			openInternalTab(routes.designSystem(designSystem.id, designSystem.title));
		} else {
			trackDesignSystemAction('open');
			navigate(routes.designSystem(designSystem.id, designSystem.title));
		}
	};

	const onMouseEnter = () => {
		dispatch(setCursorOverSidebar(true));
		// While the force-close flag is high (or its release is pending), refuse to flip
		// `sidebarHovered` true. This is what makes the synthetic mouseEnter from a
		// portal-close above the sidebar a no-op.
		if (forceTransientSidebarClosed || forceTransientSidebarReleasePending) return;
		if (!isMobile && !sidebarDocked) {
			dispatch(setSidebarHovered(true));
		}
	};

	const onMouseLeave = () => {
		// Don't close sidebar if context menu is open (user is right-clicking)
		//
		// This is part of our compromise solution to handle context menu interactions in the sidebar.
		// The fundamental challenge is that, when the mouse enters the context menu, it triggers a
		// mouseLeave event on the sidebar.
		//
		// TLDR we can't detect things cleanly without a major refactor of context menu.
		// So accepting compromise:
		//   - Block closing while a context menu is open.
		//   - When the menu closes (accept or cancel), the sidebar stays open until the user hovers away
		//   - Sometimes this means the user has to hover onto, and then off of the sidebar to close it.
		//
		// This is not ideal but, this is not a time to let perfect be the enemy of good.
		// This is not a frequent interaction after all...
		//
		// ALTERNATIVES CONSIDERED:
		// 1. Close sidebar immediately when menu closes - feels abrupt, especially when making a selection
		// 2. Detect hover state when menu closes (via refs, timers, :hover) - unreliable, complex
		// 3. Click-outside-to-close model - more predictable but changes the interaction paradigm
		//
		// TRADEOFF:
		// The sidebar may linger open after menu interaction until user hovers away.
		// This is acceptable because it's predictable and the code stays simple.
		dispatch(setCursorOverSidebar(false));
		// Cursor has left — if we were waiting to release the force-close flag, do it now.
		// Drop the pending bit too so we don't repeat this on subsequent leaves.
		if (forceTransientSidebarReleasePending) {
			dispatch(setForceTransientSidebarReleasePending(false));
			dispatch(setForceTransientSidebarClosed(false));
		}
		if (!isMobile && !sidebarDocked && !contextMenu) {
			dispatch(setSidebarHovered(false));
		}
	};

	//----------------------------------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------------------------------

	const sortedScenes = useMemo(() => {
		const idSet = new Set(userSceneIds);
		return [...scenes]
			.filter((s: Scene) => idSet.has(s.id))
			.sort((a, b) => {
				const getMostRecentAction = (scene: Scene) => {
					const creationDate = new Date(scene.createdAt);
					if (!scene.metadata?.openActions || !user?.id) return creationDate;
					const userAction = scene.metadata.openActions[user.id];
					if (!userAction) return creationDate;
					return new Date(userAction);
				};
				return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
			});
	}, [scenes, userSceneIds, user]);

	const sortedGuestScenes = useMemo(
		() =>
			[...guestScenes].sort((a, b) => {
				const getMostRecentAction = (scene: Scene) => {
					const creationDate = new Date(scene.createdAt);
					if (!scene.metadata?.openActions || !user?.id) return creationDate;
					const userAction = scene.metadata.openActions[user.id];
					if (!userAction) return creationDate;
					return new Date(userAction);
				};
				return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
			}),
		[guestScenes, user]
	);

	const sortedDesignSystems = useMemo(() => {
		const idSet = new Set(userDesignSystemIds);
		return [...designSystems]
			.filter((ds: DesignSystem) => idSet.has(ds.id))
			.sort((a, b) => {
				const getMostRecentAction = (designSystem: DesignSystem) => {
					const creationDate = new Date(designSystem.createdAt);
					if (!designSystem.metadata?.openActions || !user?.id) return creationDate;
					const userAction = designSystem.metadata.openActions[user.id];
					if (!userAction) return creationDate;
					return new Date(userAction);
				};
				return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
			});
	}, [designSystems, userDesignSystemIds, user]);

	const sortedGuestDesignSystems = useMemo(
		() =>
			[...guestDesignSystems].sort((a, b) => {
				return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
			}),
		[guestDesignSystems]
	);

	const filter = filterText.toLowerCase();
	const filteredScenes = useMemo(
		() =>
			filter ? sortedScenes.filter((s) => s.title.toLowerCase().includes(filter)) : sortedScenes,
		[sortedScenes, filter]
	);
	const filteredGuestScenes = useMemo(
		() =>
			filter
				? sortedGuestScenes.filter((s) => s.title.toLowerCase().includes(filter))
				: sortedGuestScenes,
		[sortedGuestScenes, filter]
	);
	const filteredDesignSystems = useMemo(
		() =>
			filter
				? sortedDesignSystems.filter((ds) => ds.title.toLowerCase().includes(filter))
				: sortedDesignSystems,
		[sortedDesignSystems, filter]
	);
	const filteredGuestDesignSystems = useMemo(
		() =>
			filter
				? sortedGuestDesignSystems.filter((ds) => ds.title.toLowerCase().includes(filter))
				: sortedGuestDesignSystems,
		[sortedGuestDesignSystems, filter]
	);

	// Apply per-section limit, growing by chunk when user clicks "more"
	const sliceForSection = <T,>(items: T[], sectionKey: string): T[] =>
		items.slice(0, sectionLimits[sectionKey] || SECTION_LIMIT);

	const onShowMore = (sectionKey: string) => {
		setSectionLimits((prev) => ({
			...prev,
			[sectionKey]: (prev[sectionKey] || SECTION_LIMIT) + SHOW_MORE_CHUNK,
		}));
	};

	const displayedDesignSystems = sliceForSection(filteredDesignSystems, 'myDs');
	const displayedScenes = sliceForSection(filteredScenes, 'myProjects');
	const displayedGuestDesignSystems = sliceForSection(filteredGuestDesignSystems, 'sharedDs');
	const displayedGuestScenes = sliceForSection(filteredGuestScenes, 'sharedProjects');

	const remainingDesignSystems = filteredDesignSystems.length - displayedDesignSystems.length;
	const remainingGuestDesignSystems =
		filteredGuestDesignSystems.length - displayedGuestDesignSystems.length;
	const remainingProjects = filteredScenes.length - displayedScenes.length;
	const remainingGuestProjects = filteredGuestScenes.length - displayedGuestScenes.length;

	return (
		<>
			{isMobile && sidebarVisible && (
				<S.Overlay onClick={() => dispatch(setSidebarVisible(false))} />
			)}
			<S.Base
				isMobile={isMobile}
				sidebarVisible={sidebarVisible}
				sidebarDocked={sidebarDocked}
				sidebarWidth={sidebarWidth}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				{isMobile ? (
					<S.MobileHeader>
						<S.MobileHeaderContent>
							<S.LogoIconWrapper>
								<Icon type={IconType.MOONCHILD} width={24} height={24} color={theme.gray[1000]} />
							</S.LogoIconWrapper>
							<S.Moonchild>
								<Logo height={18} color={theme.gray[1000]} />
							</S.Moonchild>
						</S.MobileHeaderContent>
						<S.CloseButton onClick={() => dispatch(setSidebarVisible(false))}>
							<Icon type={IconType.CLOSE} width={20} height={20} color={theme.gray[1000]} />
						</S.CloseButton>
					</S.MobileHeader>
				) : (
					<S.DesktopHeader>
						<div />
						<S.DockButton data-testid="sidebar-dock" onClick={() => dispatch(setSidebarDocked(!sidebarDocked))}>
							<Icon
								type={sidebarDocked ? IconType.SIDEBAR_UNFOLD : IconType.SIDEBAR_FOLD}
								width={22}
								height={22}
								color={theme.gray[800]}
							/>
						</S.DockButton>
					</S.DesktopHeader>
				)}
				<NewButton />
				{!isMobile && (
					<S.FilterWrapper>
						<S.FilterIcon>
							<Icon type={IconType.SEARCH} width={14} height={14} />
						</S.FilterIcon>
						<S.FilterInput
							placeholder="Filter..."
							value={filterText}
							onChange={(e) => setFilterText(e.target.value)}
							aria-label="Filter items"
						/>
						{filterText && (
							<S.FilterClearButton onClick={() => setFilterText('')} aria-label="Clear filter">
								<Icon type={IconType.CLOSE} width={14} height={14} />
							</S.FilterClearButton>
						)}
					</S.FilterWrapper>
				)}
				<S.Content>
					{loading ? (
						<S.LoadingWrapper>
							<Loader size={24} />
						</S.LoadingWrapper>
					) : (
						<>
							{!isMobile && filteredGuestDesignSystems.length > 0 && (
								<S.SectionWrapper>
									<CollapsibleSection
										label="Shared Design Systems"
										isCollapsed={!!collapsedSections['sharedDs']}
										onToggle={() => onToggleCollapse('sharedDs')}
									>
										<S.ProjectsList>
											{displayedGuestDesignSystems.map((designSystem: DesignSystem) => (
												<DesignSystemListItem
													key={designSystem.id}
													designSystem={designSystem}
													isActive={designSystem.id === activeDesignSystemId}
													onClick={() => onLoadDesignSystem(designSystem, false)}
													onCommandClick={() => onLoadDesignSystem(designSystem, true)}
												/>
											))}
											{remainingGuestDesignSystems > 0 && (
												<S.ViewAllLink onClick={() => onShowMore('sharedDs')}>
													{remainingGuestDesignSystems} more
												</S.ViewAllLink>
											)}
										</S.ProjectsList>
									</CollapsibleSection>
								</S.SectionWrapper>
							)}
							{!isMobile &&
								organizations.map((org) => {
									const orgDsKey = `orgDs-${org.id}`;
									const orgDsIds = organizationDesignSystemIds[org.id] || [];
									const orgDesignSystems = orgDsIds
										.map((dsId) => designSystems.find((ds) => ds.id === dsId))
										.filter((ds): ds is DesignSystem => ds !== undefined)
										.filter((ds) => !filter || ds.title.toLowerCase().includes(filter));
									if (orgDesignSystems.length === 0) return null;
									const displayed = sliceForSection(orgDesignSystems, orgDsKey);
									const remaining = orgDesignSystems.length - displayed.length;
									return (
										<S.SectionWrapper key={`ds-${org.id}`}>
											<CollapsibleSection
												label={`${org.name} Design Systems`}
												isCollapsed={!!collapsedSections[orgDsKey]}
												onToggle={() => onToggleCollapse(orgDsKey)}
											>
												<S.ProjectsList>
													{displayed.map((designSystem: DesignSystem) => (
														<DesignSystemListItem
															key={designSystem.id}
															designSystem={designSystem}
															isActive={designSystem.id === activeDesignSystemId}
															onClick={() => onLoadDesignSystem(designSystem, false)}
															onCommandClick={() => onLoadDesignSystem(designSystem, true)}
														/>
													))}
													{remaining > 0 && (
														<S.ViewAllLink onClick={() => onShowMore(orgDsKey)}>
															{remaining} more
														</S.ViewAllLink>
													)}
												</S.ProjectsList>
											</CollapsibleSection>
										</S.SectionWrapper>
									);
								})}
							{!isMobile && filteredDesignSystems.length > 0 && (
								<S.SectionWrapper>
									<CollapsibleSection
										label="My Design Systems"
										isCollapsed={!!collapsedSections['myDs']}
										onToggle={() => onToggleCollapse('myDs')}
									>
										<S.ProjectsList>
											{displayedDesignSystems.map((designSystem: DesignSystem) => (
												<DesignSystemListItem
													key={designSystem.id}
													designSystem={designSystem}
													isActive={designSystem.id === activeDesignSystemId}
													onClick={() => onLoadDesignSystem(designSystem, false)}
													onCommandClick={() => onLoadDesignSystem(designSystem, true)}
												/>
											))}
											{remainingDesignSystems > 0 && (
												<S.ViewAllLink onClick={() => onShowMore('myDs')}>
													{remainingDesignSystems} more
												</S.ViewAllLink>
											)}
										</S.ProjectsList>
									</CollapsibleSection>
								</S.SectionWrapper>
							)}
							{filteredGuestScenes.length > 0 && (
								<S.SectionWrapper>
									<CollapsibleSection
										label="Shared Projects"
										isCollapsed={!!collapsedSections['sharedProjects']}
										onToggle={() => onToggleCollapse('sharedProjects')}
									>
										<S.ProjectsList>
											{displayedGuestScenes.map((scene: Scene) => (
												<ProjectListItem
													key={scene.id}
													scene={scene}
													isActive={scene.id === activeSceneId}
													onClick={() => onLoadScene(scene, false)}
													onCommandClick={() => onLoadScene(scene, true)}
												/>
											))}
											{remainingGuestProjects > 0 && (
												<S.ViewAllLink onClick={() => onShowMore('sharedProjects')}>
													{remainingGuestProjects} more
												</S.ViewAllLink>
											)}
										</S.ProjectsList>
									</CollapsibleSection>
								</S.SectionWrapper>
							)}
							{organizations.map((org) => {
								const orgProjectsKey = `orgProjects-${org.id}`;
								const orgSceneIds = organizationSceneIds[org.id] || [];
								const orgScenes = orgSceneIds
									.map((sceneId) => scenes.find((s) => s.id === sceneId))
									.filter((s): s is Scene => s !== undefined)
									.filter((s) => !filter || s.title.toLowerCase().includes(filter));
								if (orgScenes.length === 0) return null;
								const displayed = sliceForSection(orgScenes, orgProjectsKey);
								const remaining = orgScenes.length - displayed.length;
								return (
									<S.SectionWrapper key={org.id}>
										<CollapsibleSection
											label={`${org.name} Projects`}
											isCollapsed={!!collapsedSections[orgProjectsKey]}
											onToggle={() => onToggleCollapse(orgProjectsKey)}
										>
											<S.ProjectsList>
												{displayed.map((scene: Scene) => (
													<ProjectListItem
														key={scene.id}
														scene={scene}
														isActive={scene.id === activeSceneId}
														onClick={() => onLoadScene(scene, false)}
														onCommandClick={() => onLoadScene(scene, true)}
													/>
												))}
												{remaining > 0 && (
													<S.ViewAllLink onClick={() => onShowMore(orgProjectsKey)}>
														{remaining} more
													</S.ViewAllLink>
												)}
											</S.ProjectsList>
										</CollapsibleSection>
									</S.SectionWrapper>
								);
							})}
							{filteredScenes.length > 0 && (
								<S.SectionWrapper>
									<CollapsibleSection
										label="My Projects"
										isCollapsed={!!collapsedSections['myProjects']}
										onToggle={() => onToggleCollapse('myProjects')}
									>
										<S.ProjectsList>
											{displayedScenes.map((scene: Scene) => (
												<ProjectListItem
													key={scene.id}
													scene={scene}
													isActive={scene.id === activeSceneId}
													onClick={() => onLoadScene(scene, false)}
													onCommandClick={() => onLoadScene(scene, true)}
												/>
											))}
											{remainingProjects > 0 && (
												<S.ViewAllLink onClick={() => onShowMore('myProjects')}>
													{remainingProjects} more
												</S.ViewAllLink>
											)}
										</S.ProjectsList>
									</CollapsibleSection>
								</S.SectionWrapper>
							)}
						</>
					)}
				</S.Content>
				{!isGuest && (
					<S.Footer>
						{!isMobile && <AccountButton />}
						{isMobile && (
							<S.ProfileSettingsRow>
								<S.ProfileButton
									onClick={() => {
										navigate(routes.profile());
										if (isMobile) {
											dispatch(setSidebarVisible(false));
										}
									}}
								>
									<ProfileIcon size={32} />
									<S.ProfileName>{user?.displayName || ''}</S.ProfileName>
								</S.ProfileButton>
							</S.ProfileSettingsRow>
						)}
					</S.Footer>
				)}
			</S.Base>
		</>
	);
};

export default Sidebar;
